import { useEffect, useRef, useState } from "react";
import { useCurrentMedia, usePromptLabels, usePredictionsForFile } from "@/store";
import { MediaItemStatus } from "@/types/media";
import { useFlowState } from "@/hooks/useFlowState";
import { Flows, BuildSteps } from "@/types/flows";
import { useAutoPredictionsTrigger } from "@/contexts/AutoPredictionsContext";
import { useLocation } from "react-router-dom";

/**
 * AutoPredictions - Automatically triggers the "Find Objects" button when a frame/image
 * is first visited in the BUILD flow. This component should be mounted inside the
 * AggregatedFlowContextProvider to have access to FlowContext.
 */
export const AutoPredictions = () => {
    const { flow, step } = useFlowState();
    const location = useLocation();
    const currentMedia = useCurrentMedia();
    const promptLabels = usePromptLabels();
    const predictionsForCurrentFile = usePredictionsForFile(currentMedia?.file.id);
    const { triggerPredictions } = useAutoPredictionsTrigger();

    // Track frames/images that have had auto-predictions generated
    const [autoGeneratedFrameIds, setAutoGeneratedFrameIds] = useState<Set<string>>(new Set());
    const isAutoGeneratingRef = useRef(false);
    const pendingTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

    // Auto-generate predictions when frame/image is first visited in BUILD flow
    useEffect(() => {
        const mediaItemForInference = currentMedia?.file?.isVideo
            ? currentMedia?.frame
            : currentMedia?.file;

        const capturedMediaItemId = mediaItemForInference?.id;
        const isInPromptStep = location.pathname.includes("/build/prompt");

        // Only trigger in PROMPT step of BUILD flow
        // Use pathname check as it's more reliable than the step state
        if (flow !== Flows.BUILD || !isInPromptStep) {
            return;
        }

        // Need prompts or object cards to generate predictions
        const hasObjectCards = promptLabels.size > 0;
        if (!hasObjectCards) {
            return;
        }

        // Need media item for inference
        if (!mediaItemForInference) {
            return;
        }

        // Check if we already auto-generated for this media item
        if (autoGeneratedFrameIds.has(mediaItemForInference.id)) {
            return;
        }

        // Check if there are already predictions for this frame/image
        const currentFrameSegment = currentMedia?.frameSegment ?? 0;
        const frameKey = currentMedia?.file?.isVideo
            ? `${currentMedia?.file.id}_${currentFrameSegment}`
            : mediaItemForInference.id;
        const currentFramePredictions = predictionsForCurrentFile?.[frameKey] || [];
        if (currentFramePredictions.length > 0) {
            return;
        }

        // Wait for upload to complete
        if (mediaItemForInference.status !== MediaItemStatus.COMPLETED) {
            return;
        }

        // Check if upload result is available (needed for inference)
        const upload =
            mediaItemForInference.uploadResizedResult || mediaItemForInference.uploadResult;
        if (!upload?.filename) {
            return;
        }

        // For videos, ensure we have all required metadata before triggering
        if (currentMedia?.file?.isVideo) {
            // Check that the parent file has natural dimensions (video metadata loaded)
            if (!currentMedia.file.metadata?.naturalDimensions) {
                return;
            }

            // Check that the frame has a segment number (required for inference)
            if (typeof mediaItemForInference.metadata?.segment !== "number") {
                return;
            }
        }

        // Cancel any existing timeout for this media item
        if (pendingTimeoutRef.current[capturedMediaItemId!]) {
            clearTimeout(pendingTimeoutRef.current[capturedMediaItemId!]);
        }

        // Wait 300ms to ensure the user has settled on this frame
        // This prevents triggering on frames the user is quickly scrubbing through
        const timeoutId = setTimeout(() => {
            // Remove from pending timeouts
            delete pendingTimeoutRef.current[capturedMediaItemId!];

            // Re-check that we're still on the same media item
            const currentMediaItem = currentMedia?.file?.isVideo
                ? currentMedia?.frame
                : currentMedia?.file;

            if (currentMediaItem?.id !== capturedMediaItemId) {
                return;
            }

            // Mark this media item as being processed NOW (right before triggering)
            setAutoGeneratedFrameIds((prev) => new Set(prev).add(capturedMediaItemId!));

            isAutoGeneratingRef.current = true;

            // Trigger predictions - calls the same method that the "Find Objects" button uses
            triggerPredictions()
                .catch((error) => {
                    console.error("[AutoPredictions] Auto-prediction failed:", error);
                    // On error, remove from the set so it can be retried
                    setAutoGeneratedFrameIds((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(capturedMediaItemId!);
                        return newSet;
                    });
                })
                .finally(() => {
                    isAutoGeneratingRef.current = false;
                });
        }, 300);

        // Store timeout ref
        pendingTimeoutRef.current[capturedMediaItemId!] = timeoutId;

        // Cleanup timeout on unmount or when dependencies change
        return () => {
            clearTimeout(timeoutId);
            delete pendingTimeoutRef.current[capturedMediaItemId!];
        };
    }, [
        flow,
        location.pathname,
        // For videos: track frame changes
        currentMedia?.frame?.id,
        currentMedia?.frame?.status,
        currentMedia?.frame?.uploadResult,
        currentMedia?.frame?.uploadResizedResult,
        currentMedia?.frame?.metadata?.segment,
        // For images: track file changes
        currentMedia?.file?.id,
        currentMedia?.file?.status,
        currentMedia?.file?.uploadResult,
        currentMedia?.file?.uploadResizedResult,
        // Common dependencies
        currentMedia?.file?.isVideo,
        currentMedia?.file?.metadata?.naturalDimensions,
        currentMedia?.frameSegment,
        promptLabels,
        autoGeneratedFrameIds,
        predictionsForCurrentFile,
        triggerPredictions
    ]);

    // This component doesn't render anything
    return null;
};
